import {
    collectionGroup,
    doc,
    getDoc,
    getDocs,
    query,
    where,
} from 'firebase/firestore';
import { makeObservable, observable } from 'mobx';

import { getFirestore } from 'services/Firebase';
import type EventDispatcher from 'stores/EventDispatcher';
import { DocumentEvents } from 'types/DocumentEvents';
import {
    DocumentRole,
    type DocumentRoleBinding,
    type MemberAccess,
    unpackDocumentRoleBindingLookup,
} from 'types/Roles';

import FirebaseFunctions from './FirebaseFunctions';
import { UserSchemaPublic } from './User';

export type MembersManagerParent = EventDispatcher & {
    context: string;
    data: MemberAccess;
};

/**
 * @class MembersManager
 * @description
 * The MembersManager class is used to manage the members of a
 * DocumentStore, and whether they can view or edit the document.
 * It is used to add, remove, and update members of a document.
 *
 * @example
 * // Create a new MembersManager
 * const membersManager = new MembersManager(documentStore);
 *
 * // Load the members of the document
 * await membersManager.load();
 *
 * // Check a user's access
 * const canEdit = membersManager.canEdit(uid);
 *
 * // Add a new member, by email - the other mutators take a uid
 * await membersManager.addMember('someone@example.com', DocumentRole.EDIT);
 */
class MembersManager {
    private members: DocumentRoleBinding[] = [];
    private readonly parent: MembersManagerParent;
    // Public profiles of the members, for display. Only users/{uid}/public/*
    // is readable by other users, so this is all we can show.
    public users: UserSchemaPublic[] = [];
    private _enabled = false;

    private readonly boundUpdate: () => Promise<void>;

    constructor(parent: MembersManagerParent) {
        this.parent = parent;
        this.boundUpdate = this.update.bind(this);

        // Read during render (members table, member modals, and canEdit /
        // canView via DocumentWithMembershipStore). All three are replaced
        // wholesale, never mutated in place, so `ref` is enough.
        makeObservable<MembersManager, 'members' | '_enabled'>(this, {
            members: observable.ref,
            users: observable.ref,
            _enabled: observable.ref,
        });
    }

    get enabled() {
        return this._enabled;
    }

    /**
     * Loads the members of the parent document.
     */
    public async load() {
        // Subscribed here rather than in the constructor: only documents
        // being viewed load their members, and update() fetches each
        // member's public info - which listed documents shouldn't pay for.
        // _enabled is only ever set here, so this subscribes once.
        if (!this._enabled) {
            this._enabled = true;
            this.parent.addEventListener(
                DocumentEvents.CHANGED,
                this.boundUpdate
            );
        }
        await this.update();
    }

    /**
     * Re-reads the members from the parent document's data.
     *
     * Reading is all the client does with the stored form. Membership
     * changes go to the backend as single (uid, role) deltas (addMember
     * and friends), and setDocumentRole there serializes them, keeping the
     * stored `members` and `memberKeys` in step. They arrive back here via
     * the parent's CHANGED event.
     */
    public async update() {
        this.members = unpackDocumentRoleBindingLookup(
            this.parent.data.members
        );
        this.users = await this.loadUsers();
    }

    public canEdit(uid: string) {
        return this.getById(uid)?.role === DocumentRole.EDIT;
    }

    public canView(uid: string) {
        switch (this.getById(uid)?.role) {
            case DocumentRole.EDIT:
            case DocumentRole.VIEW:
                return true;
            default:
                return false;
        }
    }

    public async addMember(targetEmail: string, role: DocumentRole) {
        // These three go through the backend deliberately, rather than writing
        // Firestore directly. A grant has to reach the document's ancestors so
        // the tree stays navigable - see setDocumentRole in
        // functions/src/permissions/documentbind.ts, which does it in one
        // transaction and keeps `members` and `memberKeys` in step. A direct
        // client write would also bypass _ensureContextPermission, leaving
        // firestore.rules as the only guard.
        //
        // Unlike the other two this takes an email rather than a uid, and
        // resolves it here against the public user records.

        // TODO: limit 1?
        const cgPublic = collectionGroup(getFirestore(), 'public');
        const q = query(cgPublic, where('email', '==', targetEmail));
        const userMetadata = await getDocs(q);

        const { uid } = userMetadata.docs[0].data();

        return FirebaseFunctions.addMember(this.parent, uid, role);
    }

    public async updateMember(uid: string, role: DocumentRole) {
        // Sets the role outright rather than read-modify-write, so it is safe
        // to call without knowing the current one. See addMember().
        return FirebaseFunctions.updateMember(this.parent, uid, role);
    }

    public async removeMember(uid: string) {
        // Reaches setDocumentRole with a null role. Note this removes the
        // binding on *this* document only: a grant the user holds on a
        // descendant survives, and both the backend evaluator and
        // firestore.rules will still honor it. So removing someone from a
        // Client does not revoke the Creatives beneath it. See the TODO in
        // functions/src/permissions/documentbind.ts.
        await FirebaseFunctions.removeMember(this.parent, uid);
    }

    public reset() {
        this.members = [];
        this.users = [];
    }

    private async loadUsers() {
        const db = getFirestore();
        return await Promise.all(
            this.members.map(async (binding) => {
                const userId = binding.uid;
                const docRef = doc(db, 'users', userId, 'public', 'info');
                const response = await getDoc(docRef);
                if (!response.exists()) {
                    console.error(
                        `MembersManager.loadUsers: no document at ${docRef.path}`
                    );
                }

                const publicInfo = response.data() as
                    | Partial<UserSchemaPublic>
                    | undefined;

                const user: UserSchemaPublic = {
                    ...new UserSchemaPublic(),
                    ...publicInfo,
                    uid: userId,
                };
                return user;
            })
        );
    }

    public getById(id: string): DocumentRoleBinding | undefined {
        return this.members.find((member) => member.uid === id);
    }

    public getUserByMemberId(id: string) {
        return this.users.find((user) => user.uid === id);
    }

    public getByEmail(email: string) {
        return this.users.find((user) => user.email === email);
    }
}

export default MembersManager;
